import React, { useRef, useState, memo, useCallback } from 'react';
import { Text } from 'react-konva';

export const TextElement = memo(({ 
  id,
  x, 
  y, 
  text, 
  fontSize = 16,
  fontFamily = 'Arial',
  fill = '#000',
  isBold = false,
  isItalic = false,
  isStrikethrough = false,
  isSelected,
  onSelect,
  onChange,
  onDragEnd
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const textRef = useRef();

  const fontStyle = `${isBold ? 'bold' : 'normal'}${isItalic ? ' italic' : ''}`;
  const textDecoration = isStrikethrough ? 'line-through' : '';

  // Memoized callbacks
  const handleSelect = useCallback(() => {
    onSelect(id);
  }, [id, onSelect]);

  const handleDragEnd = useCallback((e) => {
    onDragEnd(id, {
      x: e.target.x(),
      y: e.target.y(),
    });
  }, [id, onDragEnd]);

  const handleChange = useCallback((changes) => {
    onChange(id, changes);
  }, [id, onChange]);

  const handleDoubleClick = () => {
    setIsEditing(true);
    const textNode = textRef.current;
    const stage = textNode.getStage();

    // Create input
    const input = document.createElement('input');
    document.body.appendChild(input);

    // Get text position in canvas coordinates
    const textPos = { x, y };
    
    // Convert canvas coordinates to screen coordinates
    const scale = stage.scaleX();
    const stagePos = stage.position();
    const containerRect = stage.container().getBoundingClientRect();
    
    const screenX = textPos.x * scale + stagePos.x + containerRect.left;
    const screenY = textPos.y * scale + stagePos.y + containerRect.top;

    input.value = text;
    input.style.position = 'absolute';
    input.style.top = screenY + 'px';
    input.style.left = screenX + 'px';
    input.style.fontSize = Math.max(12, fontSize * scale) + 'px';
    input.style.fontWeight = isBold ? '600' : '400';
    input.style.fontStyle = isItalic ? 'italic' : 'normal';
    input.style.border = '1px solid #3d9168';
    input.style.padding = '2px 4px';
    input.style.margin = '0px';
    input.style.overflow = 'hidden';
    input.style.background = 'white';
    input.style.outline = 'none';
    input.style.resize = 'none';
    input.style.fontFamily = fontFamily;
    input.style.transformOrigin = 'left top';
    input.style.textAlign = 'left';
    input.style.color = fill;
    input.style.textDecoration = isStrikethrough ? 'line-through' : 'none';
    input.style.width = '300px';
    input.style.zIndex = '10000';
    input.style.borderRadius = '2px';

    input.focus();

    let isSaving = false;
    let currentBold = isBold;
    let currentItalic = isItalic;
    let currentStrikethrough = isStrikethrough;

    const removeInput = () => {
      if (input.parentNode) {
        input.parentNode.removeChild(input);
      }
      setIsEditing(false);
    };

    const saveText = () => {
      if (isSaving) return;
      isSaving = true;
      handleChange({ text: input.value });
      removeInput();
    };

    const handleBlur = () => {
      saveText();
    };

    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      if (cmdOrCtrl && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        const next = !currentBold;
        currentBold = next;
        input.style.fontWeight = next ? '600' : '400';
        handleChange({ isBold: next });
        return;
      }

      if (cmdOrCtrl && e.key.toLowerCase() === 'i') {
        e.preventDefault();
        const next = !currentItalic;
        currentItalic = next;
        input.style.fontStyle = next ? 'italic' : 'normal';
        handleChange({ isItalic: next });
        return;
      }

      if (cmdOrCtrl && e.shiftKey && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        const next = !currentStrikethrough;
        currentStrikethrough = next;
        input.style.textDecoration = next ? 'line-through' : 'none';
        handleChange({ isStrikethrough: next });
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        input.removeEventListener('blur', handleBlur);
        saveText();
      } else if (e.key === 'Escape') {
        input.removeEventListener('blur', handleBlur);
        removeInput();
      }
      e.stopPropagation(); // Prevent canvas shortcuts
    };

    input.addEventListener('blur', handleBlur);
    input.addEventListener('keydown', handleKeyDown);
  };

  return (
    <Text
      ref={textRef}
      x={x}
      y={y}
      text={text || 'Double-click to edit'}
      fontSize={fontSize}
      fontFamily={fontFamily}
      fontStyle={fontStyle}
      textDecoration={textDecoration}
      fill={text ? fill : '#999'}
      draggable
      onClick={handleSelect}
      onTap={handleSelect}
      onDblClick={handleDoubleClick}
      onDblTap={handleDoubleClick}
      onDragEnd={handleDragEnd}
      stroke={isSelected ? '#3d9168' : 'transparent'}
      strokeWidth={isSelected ? 1 : 0}
    />
  );
});
